// Schedule model for the automation rule a schedule row owns. Every saved
// schedule has exactly one rule; `active` says whether it runs on its cadence
// or only when an operator starts it. This module maps between the server
// automation shape and the editor draft, and mirrors the Go
// Schedule.Expression() compiler for display only.
import { bannerImageOf, defaultTemplateId } from './prepackCardModel'
import { BANNER_FILLS, BANNER_MODE_DYNAMIC, BANNER_MODE_STATIC, MAX_BANNER_SCALE, MIN_BANNER_SCALE, bannerPositionOf, effectiveBannerMode, isBannerImageUrl, isBannerScale, scheduleBannerFill } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { TIME_PATTERN, formatTimeOfDay, pad2 } from './builder/timeText'
import titleVocabulary from '../../../../../shared/pkg/prepacks/titleVocabulary.json'
import { DEFAULT_START_WORD } from '@/utils/sportLabels'

const WHITESPACE_PATTERN = /\s+/
const CLOCK_FIELD_PATTERN = /^\d{1,2}$/
const NON_UPPERCASE_RUN_PATTERN = /[^A-Z]+/g
const EDGE_UNDERSCORES_PATTERN = /^_+|_+$/g
const COUNTRY_PREFIX_PATTERN = /^(US|USA|CANADA|CA)_/
const HOST_REGION_SUFFIX_PATTERN = /(us|ca)([a-z]{2})$/
const WHITESPACE_RUN_PATTERN = /\s+/g

export const FREQUENCIES = ['Every 15 minutes', 'Every 30 minutes', 'Every hour', 'Every day', 'Weekdays', 'Every week', 'Game days', 'Custom']
// Frequencies that ignore the time field.
export const SUB_HOURLY_FREQUENCIES = new Set(['Every 15 minutes', 'Every 30 minutes', 'Every hour'])
// Set-time coverage: packs across games (league, `count`) or packs for each
// upcoming game (per_event, `packs_per_event`). Keep filled has its own cap.
export const COVERAGE_MODES = [
  { value: 'league', label: 'Across games' },
  { value: 'per_event', label: 'Per game' },
]
export const DEFAULT_TIMEZONE = 'America/New_York'
// When a schedule publishes: at a set time on its cadence (a scheduled_cron
// rule), or a lead of hours and minutes before each game's kickoff
// (event_relative, stored as `lead_minutes`). The copy names the start the
// schedule's sport uses (`scheduleStartWord`); `before_kickoff` is the
// stored token whatever the sport. Keep filled tops up on a fixed cadence.
export const TIMING_OPTIONS = [
  { value: 'set_time', label: 'At a set time' },
  { value: 'before_kickoff', label: 'Before kickoff' },
  { value: 'keep_filled', label: 'Keep filled' },
]

/** TIMING_OPTIONS with the before option named for the start: "Before tip-off". */
export function timingOptions(word = DEFAULT_START_WORD) {
  return TIMING_OPTIONS.map((option) => (option.value === 'before_kickoff' ? { ...option, label: `Before ${word}` } : option))
}

// The lead runs from 1 minute to 168 hours: Hours 0-168 and Minutes 0-59 in
// the editor, read as one clock duration.
export const MAX_LEAD_HOURS = 168
export const DEFAULT_LEAD_HOURS = 3
const MAX_LEAD_MINUTES = MAX_LEAD_HOURS * 60
const DEFAULT_LEAD_MINUTES = DEFAULT_LEAD_HOURS * 60
// When a before-kickoff schedule generates its packs (`trigger_config.generate`):
// in the run at the lead, publishing in the same run (the default, and what a
// rule without the key does), or as soon as a game is listed in the games
// window, held as scheduled packs until the lead.
const GENERATE_AT_LEAD = 'at_lead'
const GENERATE_ON_LISTING = 'on_listing'
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const MAX_PACKS_PER_RUN = 50
export const MAX_PACKS_PER_EVENT = 20
// Packs a schedule keeps across games when it stores no count, a new one
// included: the server's default too (prepacks.DefaultPackCount). At 1 every
// run made a single pack.
export const DEFAULT_PACK_COUNT = 5
export const MAX_KEEP_FILLED_PACKS = 100
export const DEFAULT_KEEP_FILLED_PACKS = 20
export const KEEP_FILLED_FREQUENCY = 'Every 15 minutes'
export const CUTOFF_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 h' },
  { value: 120, label: '2 h' },
  { value: 180, label: '3 h' },
]
export const DEFAULT_CUTOFF_MINUTES = 30
export const TERMINAL_RUN_STATUSES = new Set(['success', 'partial', 'failed', 'skipped', 'unknown'])

// Why a run made no packs, by the stage that lost them (#761): a dry run's
// `reason`, and a skipped run's `details.reason` (#896).
export const NO_PACK_TITLES = {
  no_games: 'No upcoming games found',
  // Games were found, none of the schedule's teams (issue 1015).
  no_games_for_teams: 'No games for the chosen teams right now',
  markets_unavailable: 'Markets couldn’t be loaded',
  no_games_in_scope: 'No games in the sport and time window',
  markets_dont_fit: 'No legs match the markets and sides',
  leg_mix: 'No game has these legs',
  odds_out_of_range: 'Nothing inside the odds limits',
  no_combinations: 'Legs can’t make a pack of this size',
  not_combinable: 'The sportsbook won’t combine these legs',
  // The pricing engine couldn't read the games from Sports Search (#983).
  games_unreadable: 'The games couldn’t be priced',
  // The repeat limits alone left nothing (#861).
  repeat_cap: 'Every pack repeats a player, team or market shape already at its limit on these surfaces',
  // A full shelf is no problem: the run had nothing to add.
  inventory_full: 'Already stocked',
  // A Keep filled check used its run budget before it saved a pack; it is
  // no failure, and the next check continues (prod, 2026-11-02).
  out_of_time: 'Ran out of time; continues on the next check',
}

export function cronExpression(schedule) {
  if (schedule.frequency === 'Custom') return schedule.cron || ''
  if (schedule.frequency === 'Every 15 minutes') return '*/15 * * * *'
  if (schedule.frequency === 'Every 30 minutes') return '*/30 * * * *'
  const [hour, minute] = String(schedule.time || '09:00').split(':').map(Number)
  const dayOfWeek = schedule.frequency === 'Weekdays'
    ? '1-5'
    : schedule.frequency === 'Every week'
      ? Number(schedule.weekday ?? 1)
      : '*'
  return `${minute || 0} ${schedule.frequency === 'Every hour' ? '*' : hour || 0} * * ${dayOfWeek}`
}

// Day chips run Monday to Sunday; cron numbers the days 0 (Sunday) to 6.
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]
export const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
// A run time as the rule stores it ("HH:MM" on a 24-hour clock), defined with the time fields.
export { TIME_PATTERN }

function dayCron(time, days) {
  const [hour, minute] = time.split(':').map(Number)
  return `${minute} ${hour} * * ${days.join(',')}`
}

function sortedDays(days) {
  return [...new Set((days || []).map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))].sort((a, b) => a - b)
}

// One day-of-week item: a day (0-6) or an ascending range of them (`1-5`).
const DAY_ITEM = '[0-6](?:-[0-6])?'
const DAY_FIELD = new RegExp(`^${DAY_ITEM}(?:,${DAY_ITEM})*$`)

/** The days a day-of-week field names (`*`, `1-5`, `1,3-5`), else null. */
function parseDayField(field) {
  if (field === '*') return [0, 1, 2, 3, 4, 5, 6]
  if (!DAY_FIELD.test(field)) return null
  const days = []
  for (const item of field.split(',')) {
    const [start, end = start] = item.split('-').map(Number)
    if (start > end) return null
    for (let day = start; day <= end; day += 1) days.push(day)
  }
  return sortedDays(days)
}

/**
 * `{ time, days }` for a cron that runs once a day at one time on some days
 * of the week (`30 7 * * 1,3,5`, `30 19 * * 1-5`, `0 9 * * *`), else null.
 * Steps, day names, day-of-month or month fields, and several minutes or
 * hours are not one time on chosen days, so they stay a raw custom cron.
 */
export function parseDayCron(cron) {
  const fields = String(cron || '').trim().split(WHITESPACE_PATTERN)
  if (fields.length !== 5) return null
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields
  if (dayOfMonth !== '*' || month !== '*') return null
  if (!CLOCK_FIELD_PATTERN.test(minute) || !CLOCK_FIELD_PATTERN.test(hour)) return null
  if (Number(minute) > 59 || Number(hour) > 23) return null
  const days = parseDayField(dayOfWeek)
  return days ? { time: `${pad2(hour)}:${pad2(minute)}`, days } : null
}

/** "Mon, Wed, Fri" in chip order. */
export function formatDayList(days) {
  const set = new Set(days)
  return WEEK_ORDER.filter((day) => set.has(day)).map((day) => SHORT_WEEKDAYS[day]).join(', ')
}

/**
 * The frequency a day list reads as (every day, weekdays), else the list:
 * "Every day", "Weekdays", "Mon, Wed, Fri".
 */
export function dayListLabel(days) {
  const key = sortedDays(days).join(',')
  if (key === '0,1,2,3,4,5,6') return 'Every day'
  if (key === '1,2,3,4,5') return 'Weekdays'
  return formatDayList(days)
}

/**
 * Days (0-6) the schedule runs on, or null when it does not run on a list of
 * days: sub-hourly, game days, or a custom cron parseDayCron cannot read.
 */
export function scheduleDays(schedule) {
  switch (schedule?.frequency) {
    case 'Every day': return [0, 1, 2, 3, 4, 5, 6]
    case 'Weekdays': return [1, 2, 3, 4, 5]
    case 'Every week': return [Number(schedule.weekday ?? 1)]
    case 'Custom': return parseDayCron(schedule.cron)?.days || null
    default: return null
  }
}

/** The time a day-based schedule runs at: a custom day cron's own time, else the time field. */
export function scheduleTime(schedule) {
  if (schedule?.frequency === 'Custom') return parseDayCron(schedule.cron)?.time || schedule.time || ''
  return schedule?.time || ''
}

/**
 * The schedule running on `days` at its time: an existing frequency when one
 * matches (every day, weekdays, one day a week), else Custom with a cron
 * compiled from the time and the days. An empty list changes nothing.
 */
export function scheduleWithDays(schedule, days) {
  const unique = sortedDays(days)
  if (unique.length === 0) return schedule
  const current = scheduleTime(schedule)
  const time = TIME_PATTERN.test(current) ? current : '09:00'
  const base = { ...schedule, time }
  if (unique.length === 7) return { ...base, frequency: 'Every day' }
  if (unique.join(',') === '1,2,3,4,5') return { ...base, frequency: 'Weekdays' }
  if (unique.length === 1) return { ...base, frequency: 'Every week', weekday: unique[0] }
  return { ...base, frequency: 'Custom', cron: dayCron(time, unique) }
}

/** The schedule at a new time; a custom day cron is recompiled so the two never disagree. */
export function scheduleWithTime(schedule, time) {
  const parsed = schedule.frequency === 'Custom' ? parseDayCron(schedule.cron) : null
  if (parsed && TIME_PATTERN.test(time)) return { ...schedule, time, cron: dayCron(time, parsed.days) }
  return { ...schedule, time }
}

/** Whether a draft publishes before each kickoff rather than at a set time. */
export function isBeforeKickoff(schedule) {
  return schedule?.timing === 'before_kickoff'
}

/** Whether a stored rule publishes before each kickoff (an event_relative rule). */
export function isBeforeKickoffRule(automation) {
  return automation?.trigger_type === 'event_relative'
}

/** Whether a draft keeps its connected surfaces filled. */
export function isKeepFilled(schedule) {
  return schedule?.timing === 'keep_filled'
}

/** Keep filled uses a set-time rule whose coverage names the mode. */
export function isKeepFilledRule(automation) {
  return automation?.criteria?.coverage === 'keep_filled'
}

// A stepper value as a number; blank or missing is not one.
const leadPart = (value) => (value === '' || value === null || value === undefined ? NaN : Number(value))

/** The lead a before-kickoff draft publishes ahead of each kickoff, in minutes: hours × 60 + minutes. */
export function leadMinutesOf(schedule) {
  return leadPart(schedule?.leadHours) * 60 + leadPart(schedule?.leadMinutes)
}

/** Whether the draft's Hours (0-168) and Minutes (0-59) are whole numbers making a lead of 1 minute to 168 hours. */
function leadInRange(schedule) {
  const hours = leadPart(schedule?.leadHours)
  const minutes = leadPart(schedule?.leadMinutes)
  const lead = hours * 60 + minutes
  return Number.isInteger(hours) && hours >= 0 && hours <= MAX_LEAD_HOURS
    && Number.isInteger(minutes) && minutes >= 0 && minutes <= 59
    && lead >= 1 && lead <= MAX_LEAD_MINUTES
}

/**
 * The lead a stored before-kickoff rule publishes ahead of kickoff, in
 * minutes: `lead_minutes`, or `hours_before` × 60 on a rule saved before
 * leads had minutes. The default 3 hours when the rule holds no whole number
 * above zero.
 */
export function triggerLeadMinutes(trigger) {
  const minutes = trigger?.lead_minutes != null ? Number(trigger.lead_minutes) : Number(trigger?.hours_before) * 60
  return Number.isInteger(minutes) && minutes > 0 ? minutes : DEFAULT_LEAD_MINUTES
}

/**
 * The lead a stored rule holds, in minutes, read as triggerLeadMinutes reads
 * it but kept when it is 0 (a draft's unfinished lead); null without a whole
 * number of minutes.
 */
function storedTriggerLead(trigger) {
  const raw = trigger?.lead_minutes != null ? trigger.lead_minutes : trigger?.hours_before != null ? Number(trigger.hours_before) * 60 : null
  const minutes = raw === null ? NaN : Number(raw)
  return Number.isInteger(minutes) && minutes >= 0 ? minutes : null
}

/** Whether a stored before-kickoff rule generates its packs early, when each game is listed; a rule without the key does not. */
export function triggerGeneratesEarly(trigger) {
  return trigger?.generate === GENERATE_ON_LISTING
}

/** A lead as the Schedules table and the help text write it: "2 h 30 min", "45 min", "5 h". */
export function leadLabel(minutes) {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return [hours ? `${hours} h` : '', rest ? `${rest} min` : ''].filter(Boolean).join(' ')
}

const unitText = (count, unit) => `${count} ${unit}${count === 1 ? '' : 's'}`

/** A lead in words: "2 hours 30 minutes", "45 minutes", "1 hour". */
export function leadInWords(minutes) {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return [hours ? unitText(hours, 'hour') : '', rest ? unitText(rest, 'minute') : ''].filter(Boolean).join(' ')
}

// The example line's game kicks off at 19:00 on the schedule's clock.
const SAMPLE_KICKOFF_MINUTE = 19 * 60
const MINUTES_PER_DAY = 24 * 60
const clockTime = (minuteOfDay) => `${pad2(Math.floor(minuteOfDay / 60))}:${pad2(minuteOfDay % 60)}`

/**
 * A worked example of the lead for a game kicking off at 19:00:
 * `{ kickoff, publish, daysEarlier }`, the times written by `formatter`
 * (`timeFormatter` in builder/timeText.js; the operator's locale when
 * omitted) and `daysEarlier` the calendar days the publish time falls before
 * kickoff day (a 20-hour lead: 1). Null while the lead is out of range.
 */
export function kickoffExample(schedule, formatter) {
  if (!leadInRange(schedule)) return null
  const publish = SAMPLE_KICKOFF_MINUTE - leadMinutesOf(schedule)
  const daysEarlier = publish < 0 ? Math.ceil(-publish / MINUTES_PER_DAY) : 0
  return {
    kickoff: formatTimeOfDay(clockTime(SAMPLE_KICKOFF_MINUTE), formatter),
    publish: formatTimeOfDay(clockTime(publish + daysEarlier * MINUTES_PER_DAY), formatter),
    daysEarlier,
  }
}

/** The schedule in one line; `word` names the start ("Publishes 3 hours before each tip-off"). */
export function compiledSchedule(schedule, word = DEFAULT_START_WORD) {
  if (isKeepFilled(schedule)) return `Keep filled · up to ${schedule.keepFilledCount ?? DEFAULT_KEEP_FILLED_PACKS} packs`
  if (isBeforeKickoff(schedule)) {
    const lead = leadMinutesOf(schedule)
    return Number.isInteger(lead) && lead > 0 ? `Publishes ${leadInWords(lead)} before each ${word}` : `Publishes before each ${word}`
  }
  if (schedule.frequency === 'Custom') {
    const parsed = parseDayCron(schedule.cron)
    if (!parsed) return 'Runs on the custom schedule'
    const named = dayListLabel(parsed.days)
    if (named === 'Every day') return `Runs every day at ${parsed.time}`
    if (named === 'Weekdays') return `Runs every weekday at ${parsed.time}`
    return `Runs ${named} at ${parsed.time}`
  }
  if (schedule.frequency === 'Every 15 minutes') return 'Checks the shelf every 15 minutes'
  if (schedule.frequency === 'Every 30 minutes') return 'Checks the shelf every 30 minutes'
  if (schedule.frequency === 'Every hour') return 'Runs every hour'
  if (schedule.frequency === 'Weekdays') return `Runs every weekday at ${schedule.time}`
  if (schedule.frequency === 'Every week') return `Runs every ${WEEKDAYS[Number(schedule.weekday ?? 1)] || 'week'} at ${schedule.time}`
  if (schedule.frequency === 'Game days') return `Runs on game days at ${schedule.time}`
  return `Runs every day at ${schedule.time}`
}

/**
 * Cadence label for the schedules table; `word` names the start of a
 * before-kickoff schedule's games ("2 h before tip-off"). Null when the
 * schedule only runs on demand: no rule yet, or a rule that is paused.
 */
export function scheduleSummary(automation, word = DEFAULT_START_WORD) {
  if (!automation || automation.is_active === false) return null
  if (isKeepFilledRule(automation)) return `Keep filled · up to ${automation.criteria.count ?? DEFAULT_KEEP_FILLED_PACKS} packs`
  const trigger = automation.trigger_config || {}
  if (isBeforeKickoffRule(automation)) {
    return `${leadLabel(triggerLeadMinutes(trigger))} before ${word}${triggerGeneratesEarly(trigger) ? ' · generated early' : ''}`
  }
  const frequency = trigger.frequency || 'Every day'
  if (frequency === 'Custom') return `Custom · ${trigger.cron || ''}`.trim()
  if (SUB_HOURLY_FREQUENCIES.has(frequency)) return frequency
  return `${frequency} · ${trigger.time || '09:00'}`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MS_PER_DAY = 24 * 60 * 60 * 1000

/** The zone a saved rule's times are in: its own, else the one the worker defaults to. */
export function ruleTimezone(automation) {
  return automation?.trigger_config?.timezone || DEFAULT_TIMEZONE
}

const wallClockFormatters = new Map()
function wallClockFormatter(zone) {
  if (!wallClockFormatters.has(zone)) {
    let formatter = null
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZoneName: 'short',
      })
    } catch {
      formatter = null
    }
    wallClockFormatters.set(zone, formatter)
  }
  return wallClockFormatters.get(zone)
}

// `date` as a clock in `zone` reads it: its calendar day (`dayNumber`, days
// since the epoch, to compare days), weekday, "HH:MM" and the zone's short
// name. The viewer's clock, unnamed, for a zone the runtime does not know.
function wallClock(date, zone) {
  const formatter = wallClockFormatter(zone)
  if (!formatter) {
    return {
      dayNumber: Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY,
      weekday: SHORT_WEEKDAYS[date.getDay()],
      day: date.getDate(),
      month: date.getMonth(),
      time: `${pad2(date.getHours())}:${pad2(date.getMinutes())}`,
      zoneName: '',
    }
  }
  const parts = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]))
  const [year, month, day] = [Number(parts.year), Number(parts.month) - 1, Number(parts.day)]
  return {
    dayNumber: Date.UTC(year, month, day) / MS_PER_DAY,
    weekday: parts.weekday,
    day,
    month,
    time: `${parts.hour}:${parts.minute}`,
    zoneName: parts.timeZoneName || '',
  }
}

/** "EDT", "CST", "UTC": `zone`'s short name at `date`; '' for a zone the runtime does not know. */
export function zoneAbbreviation(zone, date = new Date()) {
  return wallClock(date, zone).zoneName
}

/**
 * When the worker runs a schedule next, on the schedule's clock (the zone
 * its cadence is set in) and named by it: "Today 16:30 EDT", "Tomorrow
 * 09:00 EDT", "Mon 12 Oct 19:00 EDT". Null when the schedule runs on demand
 * or has no next run stored yet.
 */
export function formatNextRun(automation, now = new Date()) {
  if (!automation || automation.is_active === false || !automation.next_run_at) return null
  const date = new Date(automation.next_run_at)
  if (Number.isNaN(date.getTime())) return null
  const zone = ruleTimezone(automation)
  const at = wallClock(date, zone)
  const days = at.dayNumber - wallClock(now, zone).dayNumber
  const when = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${at.weekday} ${at.day} ${MONTHS[at.month]}`
  return [when, at.time, at.zoneName].filter(Boolean).join(' ')
}

/**
 * Label of the one number a schedule keeps: "Packs to keep" across games,
 * "Packs per game" per game (and before kickoff), or Keep filled's cap.
 */
export function countLabel(coverage) {
  if (coverage === 'keep_filled') return 'Maximum upcoming packs'
  return coverage === 'per_event' ? 'Packs per game' : 'Packs to keep'
}

/**
 * Per-game coverage only makes sense for recipes whose packs stay inside one
 * game; a cross-game recipe would never meet the target. The server catalog
 * says which strategies qualify (`same_game`).
 */
export function coverageModesForRecipe(recipe, strategies = []) {
  const strategyId = recipe?.definition?.strategy
  const sameGame = strategies.some((item) => item?.id === strategyId && item.same_game === true)
  return sameGame ? COVERAGE_MODES : COVERAGE_MODES.filter((mode) => mode.value !== 'per_event')
}

// The IANA zone each US state and Canadian province runs on, keyed by the
// name a regulation carries (`US_MARYLAND`, `Maryland`, `US_NEW_YORK_ONLINE`)
// and by its postal code (Kambi offering codes end in it: `parxusmd`).
// States split across zones take the zone most of their population uses, and
// states that keep Eastern or Mountain time take the common zone for it.
const PLACE_TIMEZONES = [
  ['AL', 'Alabama', 'America/Chicago'], ['AK', 'Alaska', 'America/Anchorage'], ['AZ', 'Arizona', 'America/Phoenix'],
  ['AR', 'Arkansas', 'America/Chicago'], ['CA', 'California', 'America/Los_Angeles'], ['CO', 'Colorado', 'America/Denver'],
  ['CT', 'Connecticut', 'America/New_York'], ['DE', 'Delaware', 'America/New_York'], ['DC', 'District of Columbia', 'America/New_York'],
  ['FL', 'Florida', 'America/New_York'], ['GA', 'Georgia', 'America/New_York'], ['HI', 'Hawaii', 'Pacific/Honolulu'],
  ['ID', 'Idaho', 'America/Denver'], ['IL', 'Illinois', 'America/Chicago'], ['IN', 'Indiana', 'America/New_York'],
  ['IA', 'Iowa', 'America/Chicago'], ['KS', 'Kansas', 'America/Chicago'], ['KY', 'Kentucky', 'America/New_York'],
  ['LA', 'Louisiana', 'America/Chicago'], ['ME', 'Maine', 'America/New_York'], ['MD', 'Maryland', 'America/New_York'],
  ['MA', 'Massachusetts', 'America/New_York'], ['MI', 'Michigan', 'America/New_York'], ['MN', 'Minnesota', 'America/Chicago'],
  ['MS', 'Mississippi', 'America/Chicago'], ['MO', 'Missouri', 'America/Chicago'], ['MT', 'Montana', 'America/Denver'],
  ['NE', 'Nebraska', 'America/Chicago'], ['NV', 'Nevada', 'America/Los_Angeles'], ['NH', 'New Hampshire', 'America/New_York'],
  ['NJ', 'New Jersey', 'America/New_York'], ['NM', 'New Mexico', 'America/Denver'], ['NY', 'New York', 'America/New_York'],
  ['NC', 'North Carolina', 'America/New_York'], ['ND', 'North Dakota', 'America/Chicago'], ['OH', 'Ohio', 'America/New_York'],
  ['OK', 'Oklahoma', 'America/Chicago'], ['OR', 'Oregon', 'America/Los_Angeles'], ['PA', 'Pennsylvania', 'America/New_York'],
  ['PR', 'Puerto Rico', 'America/Puerto_Rico'], ['RI', 'Rhode Island', 'America/New_York'], ['SC', 'South Carolina', 'America/New_York'],
  ['SD', 'South Dakota', 'America/Chicago'], ['TN', 'Tennessee', 'America/Chicago'], ['TX', 'Texas', 'America/Chicago'],
  ['UT', 'Utah', 'America/Denver'], ['VT', 'Vermont', 'America/New_York'], ['VA', 'Virginia', 'America/New_York'],
  ['WA', 'Washington', 'America/Los_Angeles'], ['WV', 'West Virginia', 'America/New_York'], ['WI', 'Wisconsin', 'America/Chicago'],
  ['WY', 'Wyoming', 'America/Denver'],
  ['ON', 'Ontario', 'America/Toronto'], ['QC', 'Quebec', 'America/Toronto'], ['BC', 'British Columbia', 'America/Vancouver'],
  ['AB', 'Alberta', 'America/Edmonton'], ['MB', 'Manitoba', 'America/Winnipeg'], ['SK', 'Saskatchewan', 'America/Regina'],
  ['NS', 'Nova Scotia', 'America/Halifax'], ['NB', 'New Brunswick', 'America/Moncton'], ['NL', 'Newfoundland and Labrador', 'America/St_Johns'],
  ['PE', 'Prince Edward Island', 'America/Halifax'],
].map(([code, place, timezone]) => ({ code, place, timezone, key: place.toUpperCase().replace(NON_UPPERCASE_RUN_PATTERN, '_') }))
const CANADIAN_CODES = new Set(['ON', 'QC', 'BC', 'AB', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE'])
// Longest name first, so WEST_VIRGINIA never reads as VIRGINIA and
// WASHINGTON_DC as the state.
const PLACES_BY_NAME = [...PLACE_TIMEZONES, { code: 'DC', place: 'District of Columbia', timezone: 'America/New_York', key: 'WASHINGTON_DC' }]
  .sort((a, b) => b.key.length - a.key.length)

function placeFromName(name) {
  const key = String(name || '').trim().toUpperCase().replace(NON_UPPERCASE_RUN_PATTERN, '_').replace(EDGE_UNDERSCORES_PATTERN, '').replace(COUNTRY_PREFIX_PATTERN, '')
  if (!key) return null
  return PLACES_BY_NAME.find((entry) => key === entry.key || key.startsWith(`${entry.key}_`)) || null
}

function placeFromOfferingId(offeringId) {
  const match = String(offeringId || '').trim().toLowerCase().match(HOST_REGION_SUFFIX_PATTERN)
  if (!match) return null
  const code = match[2].toUpperCase()
  const entry = PLACE_TIMEZONES.find((item) => item.code === code)
  if (!entry || (match[1] === 'ca') !== CANADIAN_CODES.has(code)) return null
  return entry
}

/**
 * The timezone a regulation's place runs on: `{ timezone, place }` (place as
 * an operator reads it, "Maryland"), or null when the regulation names no
 * US state or Canadian province. Reads the regulation's name from
 * `regulations` (the regulations endpoint's records) and falls back to the
 * place code a Kambi offering code ends in (`parxusmd`).
 */
export function timezoneForRegulation(regulationId, regulations = []) {
  const id = String(regulationId || '').trim()
  if (!id) return null
  const record = (Array.isArray(regulations) ? regulations : []).find((item) => item?.offering_id === id)
  const entry = placeFromName(record?.regulation_name) || placeFromName(id) || placeFromOfferingId(id)
  return entry ? { timezone: entry.timezone, place: entry.place } : null
}

// How a schedule titles its packs (issue 685): an AI title per pack, the
// schedule's name, or one custom title every pack carries as written.
export const SCHEDULE_TITLE_MODES = Object.freeze(['ai', 'name', 'custom'])
export const MAX_SCHEDULE_TITLE_LENGTH = 40

/** A custom title on one line, as the server stores it: trimmed, each run of whitespace one space. */
export function normalizeScheduleTitle(text) {
  return String(text ?? '').replace(WHITESPACE_RUN_PATTERN, ' ').trim()
}

/**
 * How a saved rule titles its packs, read as the server reads it: the AI flag
 * (`ai_title_enabled`, all a Studio from before title modes writes) decides
 * 'ai'; otherwise `criteria.title_mode` 'custom' is 'custom' and anything
 * else is 'name'.
 */
export function scheduleTitleMode(automation) {
  if (!automation) return 'name'
  if (automation.ai_title_enabled) return 'ai'
  const mode = automation.criteria?.title_mode
  return typeof mode === 'string' && mode.trim().toLowerCase() === 'custom' ? 'custom' : 'name'
}

// The vocabulary the server's title rules read (titleVocabulary.json, shared
// with apps/studio/api/services): what names the bet, what only names the
// sport, league or slate, and the connectors set aside.
const TITLE_BET_WORDS = new Set([...titleVocabulary.market, ...titleVocabulary.bet_only])
const TITLE_PLAIN_WORDS = new Set([...titleVocabulary.sport, ...titleVocabulary.filler])
const TITLE_BET_STOPWORDS = new Set(titleVocabulary.bet_stopwords)
// Counts a title spells out, read as numbers (the shared number_words, and
// "hundred", which the server's reader handles as grammar).
const TITLE_NUMBER_WORDS = new Set([
  ...Object.keys(titleVocabulary.number_words.units),
  ...Object.keys(titleVocabulary.number_words.teens),
  ...Object.keys(titleVocabulary.number_words.tens),
  'hundred',
])
// A label glued to a title with a colon ("Spotlight: "), read as the server
// reads one (titleLabelPrefix in apps/shared/pkg/prepacks/schedule_title.go):
// what comes before its first letter or number goes with it ("🔥 Spotlight: "),
// and so does an emoji or other symbol in it ("Spotlight 🔥: ").
const TITLE_LABEL_PREFIX = /^[^\p{L}\p{N}:]*[\p{L}\p{N}][\p{L}\p{N}\p{S}\p{M}\p{Cf}&' ]{1,23}:\s+/u
// "O/U" reads as "over under"; "OU" alone stays a word of its own.
const TITLE_OVER_UNDER = /\bo\s*\/\s*u\b/gi

// A title word that reads as a number: a spelled count or digits.
const isTitleNumber = (word) => typeof word === 'string' && (TITLE_NUMBER_WORDS.has(word) || Number.isFinite(Number(word)))

// A title word is one of `set`'s, as written or as its plural.
const titleWordIn = (set, token) => set.has(token) || (token.length >= 3 && token.endsWith('s') && set.has(token.slice(0, -1)))

/**
 * Whether a title only names the bet (#1110), read as the server reads one
 * without its pack's legs (titleNamesTheBet with no context): after a
 * leading label ("Spotlight: "), connectors and numbers, every word is sport,
 * league, market or filler vocabulary, and at least one names the bet.
 * "Spotlight: NCAAF Spread Favourites" does; "Saturday Chalk" does not.
 */
export function titleNamesTheBet(text) {
  const words = String(text ?? '').trim().replace(TITLE_LABEL_PREFIX, '')
    .replace(TITLE_OVER_UNDER, ' over under ')
    .replace(/(\p{L})['’]s\b/giu, '$1')
    .toLowerCase()
    .match(/\p{N}+\.\p{N}+|[\p{L}\p{N}]+/gu) || []
  let bet = 0
  for (const [index, word] of words.entries()) {
    if (TITLE_BET_STOPWORDS.has(word) || isTitleNumber(word)) continue
    // The "point" of a number written out ("Six Point Five") is part of it.
    if (word === 'point' && isTitleNumber(words[index - 1]) && isTitleNumber(words[index + 1])) continue
    if (titleWordIn(TITLE_BET_WORDS, word)) {
      bet += 1
      continue
    }
    if (!titleWordIn(TITLE_PLAIN_WORDS, word)) return false
  }
  return bet > 0
}

/** A draft's title mode: one of SCHEDULE_TITLE_MODES, else the schedule's name. */
function scheduleTitleModeOf(schedule) {
  return SCHEDULE_TITLE_MODES.includes(schedule?.titleMode) ? schedule.titleMode : 'name'
}

/** The server's refusal of a draft's custom title (issue 685), or null. */
function customTitleError(schedule) {
  if (scheduleTitleModeOf(schedule) !== 'custom') return null
  const title = normalizeScheduleTitle(schedule.title)
  if (!title) return 'Enter a custom title'
  if (Array.from(title).length > MAX_SCHEDULE_TITLE_LENGTH) return `Keep the custom title to ${MAX_SCHEDULE_TITLE_LENGTH} characters or fewer`
  return null
}

/** The server's refusal of a draft's default title (#984), or null. */
function defaultTitleError(schedule) {
  if (scheduleTitleModeOf(schedule) !== 'ai') return null
  if (Array.from(normalizeScheduleTitle(schedule.defaultTitle)).length > MAX_SCHEDULE_TITLE_LENGTH) {
    return `Keep the default title to ${MAX_SCHEDULE_TITLE_LENGTH} characters or fewer`
  }
  return null
}

/**
 * criteria with the draft's title mode beside the AI flag, its custom title
 * only in custom mode, and its default title (#984) only in AI mode and only
 * when there is one.
 */
function withScheduleTitle(criteria, schedule) {
  const titled = { ...criteria, title_mode: scheduleTitleModeOf(schedule) }
  if (titled.title_mode === 'custom') titled.title = normalizeScheduleTitle(schedule.title)
  else delete titled.title
  const defaultTitle = normalizeScheduleTitle(schedule.defaultTitle)
  if (titled.title_mode === 'ai' && defaultTitle) titled.default_title = defaultTitle
  else delete titled.default_title
  return titled
}

/**
 * The editor's schedule for a stored rule (`automation`), or a new one from
 * `defaults` when there is none. A stored value that cannot be valid (no
 * surfaces, a lead of 0, a blank run time or cron) reads as its default, as
 * a schedule that passed the checks is shown. `asStored` reads a draft's
 * rule (Save draft) as it was saved instead, unfinished values and all, so
 * reopening or saving a draft never fills them in for the operator.
 */
export function scheduleFromAutomation(automation, defaults = {}, { asStored = false } = {}) {
  const trigger = automation?.trigger_config || {}
  const criteria = automation?.criteria || {}
  const stored = asStored && Boolean(automation)
  const keepFilled = isKeepFilledRule(automation)
  // A new schedule takes the organization's zone when there is one, else the
  // zone of the regulation it is priced in. A saved one keeps what it stored.
  const inferred = !automation && !defaults.timezone
    ? timezoneForRegulation(defaults.regulation, defaults.regulations)
    : null
  const lead = (stored ? storedTriggerLead(trigger) : null) ?? triggerLeadMinutes(trigger)
  return {
    automationId: automation?.id || null,
    raw: automation || null,
    // A draft's rule was never checked, so nothing in it counts as an
    // unchanged choice (keepsStoredDynamicBanner).
    unchecked: stored,
    // A new schedule runs on demand until the operator turns the cadence on.
    active: automation ? automation.is_active !== false : false,
    // Before kickoff has no cadence and Keep filled has a fixed one; the
    // editable cadence defaults are ready for a switch back to a set time.
    timing: keepFilled ? 'keep_filled' : isBeforeKickoffRule(automation) ? 'before_kickoff' : 'set_time',
    // The lead as the editor's Hours and Minutes steppers show it.
    leadHours: Math.floor(lead / 60),
    leadMinutes: lead % 60,
    // Before kickoff: build the packs when each game is listed and hold them
    // until the lead, rather than in one run at the lead.
    generateEarly: triggerGeneratesEarly(trigger),
    frequency: !keepFilled && FREQUENCIES.includes(trigger.frequency) ? trigger.frequency : 'Every day',
    time: stored ? trigger.time ?? '09:00' : trigger.time || '09:00',
    timezone: trigger.timezone || defaults.timezone || inferred?.timezone || DEFAULT_TIMEZONE,
    // Where the zone came from: 'saved', 'organization', 'regulation' (follows
    // the regulation until someone picks a zone), 'chosen' or 'default'.
    // Editor-only: it is not sent.
    timezoneSource: automation ? 'saved' : defaults.timezone ? 'organization' : inferred ? 'regulation' : 'default',
    weekday: trigger.weekday ?? 1,
    cron: stored ? trigger.cron ?? '0 9 * * *' : trigger.cron || '0 9 * * *',
    regulation: automation ? (criteria.regulation ?? '') : (defaults.regulation || ''),
    templateId: automation ? (criteria.template_id ?? '') : (defaults.templateId || ''),
    // How the packs get their picture (#606): null follows the template; otherwise { mode, prompt, url, fill, hidden }.
    banner: automation ? scheduleBannerOf(criteria.banner) : null,
    // A stored rule without a count runs as the default, as a new one starts.
    count: keepFilled ? DEFAULT_PACK_COUNT : criteria.count ?? DEFAULT_PACK_COUNT,
    // The cap stays separate from the set-time target so switching modes restores both.
    keepFilledCount: keepFilled ? criteria.count ?? DEFAULT_KEEP_FILLED_PACKS : DEFAULT_KEEP_FILLED_PACKS,
    cutoffMinutes: keepFilled ? criteria.cutoff_minutes ?? DEFAULT_CUTOFF_MINUTES : DEFAULT_CUTOFF_MINUTES,
    coverage: criteria.coverage === 'per_event' ? 'per_event' : 'league',
    packsPerEvent: Number(criteria.packs_per_event) || 1,
    surfaces: Array.isArray(automation?.target_surfaces) && (automation.target_surfaces.length || stored)
      ? [...automation.target_surfaces]
      : stored ? [] : [...(defaults.surfaces || [])],
    // A saved schedule keeps what it stored; a new one takes the caller's defaults.
    titleMode: automation
      ? scheduleTitleMode(automation)
      : SCHEDULE_TITLE_MODES.includes(defaults.titleMode) ? defaults.titleMode : 'name',
    // The custom title, kept in the draft while another mode is chosen.
    title: scheduleTitleMode(automation) === 'custom' && typeof criteria.title === 'string' ? normalizeScheduleTitle(criteria.title) : '',
    // The title a pack takes when no AI title passes (#984), likewise kept.
    defaultTitle: scheduleTitleMode(automation) === 'ai' && typeof criteria.default_title === 'string' ? normalizeScheduleTitle(criteria.default_title) : '',
    autoPublish: Boolean(automation ? automation.auto_publish : defaults.autoPublish),
    lastRunAt: automation?.last_run_at || null,
    executionCount: automation?.execution_count || 0,
  }
}

/**
 * `schedule` (the operator's edits) on top of another saved version of its
 * rule (`saved`, from scheduleFromAutomation): the edits stay, while the rule
 * they will be saved over, and whether that rule was ever checked, follow
 * `saved`. Keep my edits uses it after a conflict.
 */
export function withSavedRule(schedule, saved) {
  return { ...schedule, automationId: saved.automationId, raw: saved.raw, unchecked: saved.unchecked }
}

/**
 * A stored criteria.banner as the editor keeps it, or null when there is no usable choice. `mode` is null when the
 * template decides; `fill` is '' for the template's fill (issue 683); `hidden` turns the picture off and keeps the
 * rest for when it is turned on again; `position`/`scale` frame a static choice's picture (#978).
 */
function scheduleBannerOf(banner) {
  if (!banner || typeof banner !== 'object') return null
  const mode = banner.mode === BANNER_MODE_DYNAMIC || banner.mode === BANNER_MODE_STATIC ? banner.mode : null
  const fill = BANNER_FILLS.includes(banner.fill) ? banner.fill : ''
  const hidden = banner.hidden === true
  if (!mode && !fill && !hidden) return null
  return {
    mode,
    prompt: typeof banner.prompt === 'string' ? banner.prompt : '',
    url: typeof banner.url === 'string' ? banner.url : '',
    fill,
    hidden,
    // The preview a dynamic schedule picked as its example (issue 684); absent without one.
    ...(typeof banner.example_url === 'string' && banner.example_url.trim() ? { exampleUrl: banner.example_url } : {}),
    // The shared picture's framing (#978); absent without one, so the template's applies.
    ...(bannerPositionOf(banner.position) ? { position: banner.position } : {}),
    ...(isBannerScale(banner.scale) ? { scale: banner.scale } : {}),
  }
}

/**
 * A schedule's picture choice moved from one template with an image banner to another (issue 683 keeps it). Its
 * framing (#978) stays only on the schedule's own picture in an unchanged fill: the template's picture is another
 * picture, and another fill frames in another unit, so either starts over from the new template's framing, as a new
 * picture or fill does in the builder.
 */
export function bannerForTemplate(banner, fromTemplate, toTemplate) {
  if (!banner || (!('position' in banner) && !('scale' in banner))) return banner ?? null
  const ownPicture = banner.mode === BANNER_MODE_STATIC && String(banner.url || '').trim() !== ''
  if (ownPicture && scheduleBannerFill(fromTemplate, banner) === scheduleBannerFill(toTemplate, banner)) return banner
  const { position: _position, scale: _scale, ...rest } = banner
  return rest
}

export function newSchedule(defaults = {}) {
  return scheduleFromAutomation(null, defaults)
}

// Surfaces a new schedule targets by default: every enabled surface except the
// betWIRE feed and carousel, which carry editorial content rather than packs.
const EXCLUDED_DEFAULT_SURFACE_SLUGS = new Set(['default-feed', 'widget'])
const EXCLUDED_DEFAULT_SURFACE_NAME = /betwire|carousel/i

export function defaultScheduleSurfaces(surfaces = []) {
  return (Array.isArray(surfaces) ? surfaces : [])
    .filter((surface) => surface && surface.slug && surface.is_enabled !== false)
    .filter((surface) => !EXCLUDED_DEFAULT_SURFACE_SLUGS.has(String(surface.slug).toLowerCase()))
    .filter((surface) => !EXCLUDED_DEFAULT_SURFACE_NAME.test(String(surface.name || '')))
    .map((surface) => surface.slug)
}

/**
 * What a new schedule starts from, read from the configuration, surfaces and
 * regulations responses: the organization's regulation, its default card
 * template, and the surfaces a schedule targets by default (the enabled
 * entries of the configuration's own list when no enabled surface qualifies,
 * so a new schedule never starts on a surface the server refuses).
 */
export function scheduleDefaultsFromSettings(config, surfacesData, regulationsData) {
  const fromSurfaces = defaultScheduleSurfaces(surfacesData?.surfaces)
  const enabled = new Set((Array.isArray(surfacesData?.surfaces) ? surfacesData.surfaces : [])
    .filter((surface) => surface?.slug && surface.is_enabled !== false)
    .map((surface) => surface.slug))
  return {
    regulation: regulationsData?.regulation || regulationsData?.default_regulation || '',
    templateId: defaultTemplateId(config) || '',
    surfaces: fromSurfaces.length ? fromSurfaces : (config?.default_surfaces || []).filter((slug) => enabled.has(slug)),
  }
}

// Which builder section owns each schedule field, so a failed save opens the
// section that needs attention. Surfaces are chosen in Appearance.
const SCHEDULE_FIELD_SECTIONS = {
  title: 'appearance',
  default_title: 'appearance',
  timing: 'schedule',
  lead: 'schedule',
  frequency: 'schedule',
  cron: 'schedule',
  time: 'schedule',
  coverage: 'schedule',
  count: 'schedule',
  keepFilledCount: 'schedule',
  cutoffMinutes: 'schedule',
  packsPerEvent: 'schedule',
  surfaces: 'appearance',
  banner: 'appearance',
}

// Like ValidateScheduleCriteria on the server, an unchanged dynamic choice
// remains editable after the template disables AI or loses its default scene.
// A picture that was off is checked again when it is turned on (issue 683).
// `raw` is the last saved rule; a duplicate has none and must validate anew,
// and neither does a draft's rule, which no save has checked.
function keepsStoredDynamicBanner(schedule) {
  if (schedule.unchecked) return false
  const stored = schedule.raw?.criteria
  const scene = (banner) => String(banner?.prompt || '').replace(WHITESPACE_RUN_PATTERN, ' ').trim()
  return stored?.banner?.mode === BANNER_MODE_DYNAMIC
    && stored.banner.hidden !== true
    && String(stored.template_id || '').trim() === String(schedule.templateId || '').trim()
    && scene(stored.banner) === scene(schedule.banner)
}

/**
 * The server's message when a before-kickoff lead is longer than the recipe's
 * games window (`lookaheadHours`, "Games starting within"; 0 is no limit),
 * else null. A game is only listed inside the window, so a longer lead would
 * publish when the game is listed. The section shows it as the operator
 * edits either side; validateSchedule refuses the save.
 */
export function leadWindowError(schedule, lookaheadHours) {
  const lookahead = Number(lookaheadHours)
  if (!(lookahead > 0) || !(leadMinutesOf(schedule) > lookahead * 60)) return null
  return `Longer than the games window of ${lookahead} hours. Shorten it or widen "Games starting within".`
}

/** What a run does with the packs it generates: "Generated and published", or "Generated and saved as drafts" with auto-publish off. */
export function runMoments(schedule) {
  return schedule?.autoPublish ? 'Generated and published' : 'Generated and saved as drafts'
}

// The schedule's "Games starting within" choices (event_lookahead_hours, 0 is
// any date). A surface's game window offers the same list (#1065).
export const LOOKAHEAD_OPTIONS = [
  { value: 24, label: 'Next 24 hours' },
  { value: 48, label: 'Next 48 hours' },
  { value: 72, label: 'Next 72 hours' },
  { value: 168, label: 'Next 7 days' },
  { value: 0, label: 'Any date' },
]

// The games window ("Games starting within") in the help's words: whole days
// as days ("7-day"), anything else in hours ("36-hour").
// Days only from a week up, as the "Games starting within" options read
// ("Next 72 hours", "Next 7 days").
function windowLabel(hours) {
  return hours % 24 === 0 && hours >= 168 ? `${hours / 24}-day` : `${hours}-hour`
}

/**
 * The help under "Packs per game" for a before-kickoff draft: when its
 * packs are generated and when they go out. At the lead (the default) one run
 * does both, "Generated and published in one run, 2 h 30 min before each
 * kickoff." Generated early, a game's packs are built once it enters the
 * games window (`lookaheadHours`, "Games starting within"; 0 is any date, so
 * once it is listed) and published at the lead, or saved as drafts with
 * auto-publish off. A lead out of range names no amount; `word` names the
 * start ("before each tip-off"). Undefined for a set-time draft.
 */
export function generationHelp(schedule, lookaheadHours, word = DEFAULT_START_WORD) {
  if (!isBeforeKickoff(schedule)) return undefined
  const lead = leadInRange(schedule) ? leadLabel(leadMinutesOf(schedule)) : ''
  if (!schedule.generateEarly) {
    return lead ? `${runMoments(schedule)} in one run, ${lead} before each ${word}.` : `${runMoments(schedule)} in one run before each ${word}.`
  }
  const lookahead = Number(lookaheadHours)
  const generated = lookahead > 0 ? `Generated when a game enters the ${windowLabel(lookahead)} window` : 'Generated when a game is listed'
  if (!schedule.autoPublish) return `${generated} and saved as drafts.`
  return `${generated}. Published ${lead ? `${lead} ` : ''}before ${word}.`
}

/**
 * Returns null when the draft can be sent, otherwise `{ field, section, message }`.
 * The cadence is checked even for an on-demand schedule because the server
 * stores one rule per schedule and always compiles its cadence. A
 * before-kickoff schedule is checked for its lead and packs per game
 * instead, and, when `sameGame` is false (the pack type builds packs across
 * games), refused: it keeps packs per game. Without `sameGame` the server
 * makes that check. With `lookaheadHours` above zero (the recipe's "Games
 * starting within"), a lead longer than that window is refused too, with the
 * server's message: a game is only listed inside the window. `startWord`
 * names the start in the before-kickoff messages ("before tip-off"); the
 * server, which has no sport at hand, says "before the game starts".
 */
export function validateSchedule(schedule, { sameGame, templates = [], lookaheadHours, startWord = DEFAULT_START_WORD } = {}) {
  const problem = (field, message) => ({ field, section: SCHEDULE_FIELD_SECTIONS[field], message })
  if (!schedule) return problem('surfaces', 'Choose where the packs go')
  const titleError = customTitleError(schedule)
  if (titleError) return problem('title', titleError)
  const defaultError = defaultTitleError(schedule)
  if (defaultError) return problem('default_title', defaultError)
  // Keep filled fixes its cadence and checks a cap and cutoff. Before kickoff
  // checks a lead and packs per game. Every mode checks surfaces and pictures.
  if (isKeepFilled(schedule)) {
    const cap = Number(schedule.keepFilledCount ?? DEFAULT_KEEP_FILLED_PACKS)
    if (!Number.isInteger(cap) || cap < 1 || cap > MAX_KEEP_FILLED_PACKS) return problem('keepFilledCount', `Choose 1 to ${MAX_KEEP_FILLED_PACKS} packs`)
    if (!CUTOFF_OPTIONS.some((option) => option.value === Number(schedule.cutoffMinutes))) return problem('cutoffMinutes', 'Choose when to stop creating packs before kickoff')
  } else if (isBeforeKickoff(schedule)) {
    if (sameGame === false) return problem('timing', `Before ${startWord} needs a same-game pack type`)
    if (!leadInRange(schedule)) return problem('lead', `Choose a lead of 1 minute to ${MAX_LEAD_HOURS} hours before ${startWord}`)
    const window = leadWindowError(schedule, lookaheadHours)
    if (window) return problem('lead', window)
    const perEvent = Number(schedule.packsPerEvent)
    if (!Number.isInteger(perEvent) || perEvent < 1 || perEvent > MAX_PACKS_PER_EVENT) return problem('packsPerEvent', `Choose 1 to ${MAX_PACKS_PER_EVENT} packs per game`)
  } else {
    if (!FREQUENCIES.includes(schedule.frequency)) return problem('frequency', 'Choose how often it runs')
    if (schedule.frequency === 'Custom' && !String(schedule.cron || '').trim()) return problem('cron', 'Enter a cron expression')
    if (schedule.frequency !== 'Custom' && !SUB_HOURLY_FREQUENCIES.has(schedule.frequency) && !TIME_PATTERN.test(String(schedule.time || ''))) return problem('time', 'Choose a run time')
    if (schedule.coverage && !COVERAGE_MODES.some((mode) => mode.value === schedule.coverage)) return problem('coverage', 'Choose what the schedule keeps')
    // One number per coverage: packs per game, or packs to keep across games.
    if (schedule.coverage === 'per_event') {
      const perEvent = Number(schedule.packsPerEvent)
      if (!Number.isInteger(perEvent) || perEvent < 1 || perEvent > MAX_PACKS_PER_EVENT) return problem('packsPerEvent', `Choose 1 to ${MAX_PACKS_PER_EVENT} packs per game`)
    } else {
      const count = Number(schedule.count)
      if (!Number.isInteger(count) || count < 1 || count > MAX_PACKS_PER_RUN) return problem('count', `Choose 1 to ${MAX_PACKS_PER_RUN} packs`)
    }
  }
  if (!Array.isArray(schedule.surfaces) || schedule.surfaces.length === 0) return problem('surfaces', 'Select at least one surface')
  // The server refuses the same: a schedule's own picture is an https URL, its fill is the header or the whole card,
  // and a new picture per pack needs a template that allows AI and a scene on either side, unless the picture is off.
  const ownUrl = schedule.banner?.mode === BANNER_MODE_STATIC ? String(schedule.banner.url || '').trim() : ''
  if (ownUrl && !isBannerImageUrl(ownUrl)) return problem('banner', 'The banner picture must be an https URL')
  const exampleUrl = schedule.banner?.mode === BANNER_MODE_DYNAMIC ? String(schedule.banner.exampleUrl || '').trim() : ''
  if (exampleUrl && !isBannerImageUrl(exampleUrl)) return problem('banner', 'The example picture must be an https URL')
  if (schedule.banner?.fill && !BANNER_FILLS.includes(schedule.banner.fill)) return problem('banner', 'The picture fills the header or the whole card')
  // The framing box writes only valid values; a hand-edited draft is refused as the server refuses it (#978).
  const framed = schedule.banner?.mode === BANNER_MODE_STATIC ? schedule.banner : null
  if (framed?.position && !bannerPositionOf(framed.position)) return problem('banner', 'The banner picture position must be two percentages')
  if (framed?.scale && !isBannerScale(framed.scale)) return problem('banner', `The banner picture zoom must be between ${MIN_BANNER_SCALE} and ${MAX_BANNER_SCALE}`)
  const image = schedule.banner?.mode === BANNER_MODE_DYNAMIC && schedule.banner.hidden !== true
    ? bannerImageOf((Array.isArray(templates) ? templates : []).find((template) => template?.id === schedule.templateId))
    : null
  if (image && !keepsStoredDynamicBanner(schedule)) {
    if (!image.ai_allowed) return problem('banner', "This template doesn't allow AI pictures")
    if (!String(schedule.banner.prompt || '').trim() && !image.prompt) return problem('banner', 'Describe the scene for new pictures')
  }
  return null
}

// Trigger keys only one kind of rule reads: a payload drops the other kind's.
// `hours_before` was the lead's key before minutes. A stored one is never
// resent as it was; the payload writes it fresh as the lead's whole hours
// (at least 1) so an API from before minutes still reads the lead.
const CADENCE_KEYS = ['frequency', 'time', 'weekday', 'cron']
const LEGACY_LEAD_KEYS = ['hours_before']
const BEFORE_KICKOFF_KEYS = ['timing', 'lead_minutes', 'generate', ...LEGACY_LEAD_KEYS]

function storedTrigger(schedule, dropped) {
  const trigger = { ...(schedule.raw?.trigger_config || {}) }
  for (const key of dropped) delete trigger[key]
  return trigger
}

/**
 * Body for PUT /prepacks/recipes/:id/schedule. A schedule only says where,
 * how many and which template; scope, sizes and price bands live on the
 * definition, and the definition itself is pinned server-side from the route.
 * A before-kickoff schedule sends `trigger_type: 'event_relative'` with its
 * lead in minutes and when it generates (`generate`), and keeps packs per
 * game, a run covering one game; the draft's cadence stays in the editor for
 * a switch back to a set time.
 */
export function toSchedulePayload(schedule) {
  const beforeKickoff = isBeforeKickoff(schedule)
  const keepFilled = isKeepFilled(schedule)
  const storedCriteria = schedule.raw?.criteria
  const perEvent = Number(schedule.packsPerEvent) || 1
  const count = Number(schedule.count)
  const criteria = {
    ...(storedCriteria || {}),
    regulation: schedule.regulation ?? '',
    // A set-time per-game schedule keeps sending its stored count, which the
    // server no longer reads, so a server from before one target per coverage
    // (it read it as a cap per run) runs it as it did. The field is hidden
    // there, so a count left out of range by the other coverage goes as the
    // default instead of blocking the save.
    count: keepFilled
      ? Number(schedule.keepFilledCount ?? DEFAULT_KEEP_FILLED_PACKS)
      : beforeKickoff
        ? perEvent
        : schedule.coverage === 'per_event' && !(Number.isInteger(count) && count >= 1 && count <= MAX_PACKS_PER_RUN)
          ? DEFAULT_PACK_COUNT
          : count || DEFAULT_PACK_COUNT,
    coverage: keepFilled ? 'keep_filled' : beforeKickoff || schedule.coverage === 'per_event' ? 'per_event' : 'league',
    packs_per_event: !keepFilled && (beforeKickoff || schedule.coverage === 'per_event') ? perEvent : 1,
  }
  if (keepFilled) criteria.cutoff_minutes = Number(schedule.cutoffMinutes ?? DEFAULT_CUTOFF_MINUTES)
  else delete criteria.cutoff_minutes
  const hasStoredCriteria = Boolean(storedCriteria && typeof storedCriteria === 'object')
  // An absent stored template remains absent until an operator selects one.
  if (!hasStoredCriteria || Object.prototype.hasOwnProperty.call(storedCriteria, 'template_id') || schedule.templateId) {
    criteria.template_id = schedule.templateId || null
  }
  // Only the field the mode uses, the fill when it overrides the template's and the picture off when it is (issue
  // 683), so a schedule saved before either sends what it did; without a choice the stored one goes too, and the
  // template decides.
  const banner = schedule.banner
  const bannerMode = banner?.mode === BANNER_MODE_DYNAMIC || banner?.mode === BANNER_MODE_STATIC ? banner.mode : null
  const bannerFill = BANNER_FILLS.includes(banner?.fill) ? banner.fill : ''
  const pictureOff = banner?.hidden === true
  if (bannerMode || bannerFill || pictureOff) {
    // A multi-line scene goes as the one line the server stores, so a saved draft is not dirty again (issue 684).
    const value = bannerMode ? String((bannerMode === BANNER_MODE_DYNAMIC ? banner.prompt : banner.url) || '').replace(WHITESPACE_RUN_PATTERN, ' ').trim() : ''
    // The preview a dynamic schedule picked as its example (issue 684); packs never get it.
    const example = bannerMode === BANNER_MODE_DYNAMIC ? String(banner.exampleUrl || '').trim() : ''
    // The shared picture's framing (#978): a static choice's only, as the server keeps it.
    const framed = bannerMode === BANNER_MODE_STATIC ? banner : null
    criteria.banner = {
      ...(bannerMode ? { mode: bannerMode } : {}),
      ...(value ? { [bannerMode === BANNER_MODE_DYNAMIC ? 'prompt' : 'url']: value } : {}),
      ...(bannerFill ? { fill: bannerFill } : {}),
      ...(pictureOff ? { hidden: true } : {}),
      ...(example ? { example_url: example } : {}),
      ...(bannerPositionOf(framed?.position) ? { position: framed.position } : {}),
      ...(isBannerScale(framed?.scale) ? { scale: framed.scale } : {}),
    }
  } else {
    delete criteria.banner
  }
  return {
    is_active: Boolean(schedule.active),
    trigger_type: beforeKickoff ? 'event_relative' : 'scheduled_cron',
    trigger_config: keepFilled
      ? {
        ...storedTrigger(schedule, [...CADENCE_KEYS, ...BEFORE_KICKOFF_KEYS]),
        frequency: KEEP_FILLED_FREQUENCY,
        timezone: schedule.timezone || DEFAULT_TIMEZONE,
      }
      : beforeKickoff
      ? {
        ...storedTrigger(schedule, [...CADENCE_KEYS, ...LEGACY_LEAD_KEYS]),
        mode: 'builder',
        timing: 'before_kickoff',
        lead_minutes: leadMinutesOf(schedule),
        hours_before: Math.max(1, Math.floor(leadMinutesOf(schedule) / 60)),
        generate: schedule.generateEarly ? GENERATE_ON_LISTING : GENERATE_AT_LEAD,
        timezone: schedule.timezone || DEFAULT_TIMEZONE,
      }
      : {
        ...storedTrigger(schedule, BEFORE_KICKOFF_KEYS),
        mode: 'builder',
        frequency: schedule.frequency,
        time: schedule.time,
        timezone: schedule.timezone || DEFAULT_TIMEZONE,
        weekday: Number(schedule.weekday ?? 1),
        cron: cronExpression(schedule),
      },
    criteria: withScheduleTitle(criteria, schedule),
    auto_publish: Boolean(schedule.autoPublish),
    ai_title_enabled: scheduleTitleModeOf(schedule) === 'ai',
    target_surfaces: [...(schedule.surfaces || [])],
  }
}

/** "Last run Oct 14, 9:00 AM EDT · 3 runs", on the schedule's clock as its next run and cadence are. */
export function formatLastRun(automation) {
  if (!automation?.last_run_at) return 'Never run'
  const date = new Date(automation.last_run_at)
  if (Number.isNaN(date.getTime())) return 'Never run'
  const options = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
  let stamp
  try {
    stamp = date.toLocaleString(undefined, { ...options, timeZone: ruleTimezone(automation), timeZoneName: 'short' })
  } catch {
    stamp = date.toLocaleString(undefined, options)
  }
  const runs = automation.execution_count || 0
  return `Last run ${stamp} · ${runs} run${runs === 1 ? '' : 's'}`
}

/**
 * What a schedule's packs show with this template: the effective mode and, when they share one, whose picture. A
 * picture turned off shows none whatever the template does, so a template change never lists it.
 */
function scheduleBannerOutcome(template, banner) {
  if (banner?.hidden === true) return { mode: 'hidden', picture: 'none' }
  const mode = effectiveBannerMode(template, banner)
  if (mode !== BANNER_MODE_STATIC) return { mode, picture: '' }
  if (banner?.mode === BANNER_MODE_STATIC && String(banner.url || '').trim()) return { mode, picture: 'schedule' }
  return { mode, picture: bannerImageOf(template)?.url ? 'template' : 'none' }
}

/**
 * The schedules whose pictures change when the template's saved picture settings become the draft's (#606), by
 * change: `toStatic` (New picture per pack becomes the same picture), `toColors` (the template's picture is gone, so
 * the card's colors show), `toDynamic` (the template's picture by default becomes a new picture per pack) and
 * `toNone` (the banner is no longer a picture, so packs that showed one show the card's colors).
 * Paused schedules count: they can still run on demand. Names are the schedules' names.
 */
export function scheduleBannerChanges(recipes, savedTemplate, draftTemplate) {
  const changes = { toStatic: [], toColors: [], toDynamic: [], toNone: [] }
  const id = savedTemplate?.id
  if (!id || !Array.isArray(recipes)) return changes
  for (const recipe of recipes) {
    const criteria = recipe?.automation?.criteria
    if (!criteria || criteria.template_id !== id) continue
    const banner = scheduleBannerOf(criteria.banner)
    const before = scheduleBannerOutcome(savedTemplate, banner)
    const after = scheduleBannerOutcome(draftTemplate, banner)
    const name = recipe.name || recipe.id || 'A schedule'
    const showedPicture = before.mode === BANNER_MODE_DYNAMIC || (before.mode === BANNER_MODE_STATIC && before.picture !== 'none')
    if (showedPicture && !after.mode) changes.toNone.push(name)
    else if (before.mode === BANNER_MODE_DYNAMIC && after.mode === BANNER_MODE_STATIC) changes.toStatic.push(name)
    else if (before.picture === 'template' && after.picture === 'none') changes.toColors.push(name)
    else if (before.picture === 'template' && !banner?.mode && after.mode === BANNER_MODE_DYNAMIC) changes.toDynamic.push(name)
  }
  return changes
}
